import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"

import { userAtom } from "@/atoms/userAtom"

import { ECommercePlatforms, EcommerceIntegration } from "@/types/ecommerce"

import { Loader } from "@/UI"
import SettingSection from "../../shared/SettingSection"
import SettingContentWrapper from "../../shared/SettingContentWrapper"
import EcommercePlatformsList from "./components/EcommercePlatformsList"
import { isEmpty } from "lodash"
import EcommerceShopSettings from "../ProviderSettings/components/EcommerceShopSettings/EcommerceShopSettings"
import { useFlags } from "flagsmith/react"
import { useEcommerce } from "@/hooks/useEcommerce"

interface EcommerceSettingsProps {}

const EcommerceSettings: React.FC<EcommerceSettingsProps> = () => {
  const { t } = useTranslation()
  const flags = useFlags(["ecommerce_platforms_enabled"])
  const enabledPlatforms = JSON.parse((flags.ecommerce_platforms_enabled.value as string) || "{}")

  const user = useRecoilValue(userAtom)

  const { ecommerceIntegrations, setEcommerceIntegrations, fetchEcommerceIntegrations, isLoading } =
    useEcommerce()

  const [storeFocus, setStoreFocus] = useState<EcommerceIntegration | null>()

  const platformsConfig = [
    {
      platform: ECommercePlatforms.SHOPIFY,
      icon: "/assets/images/ecommerce/shopify.svg",
      active: enabledPlatforms?.shopify,
      visible: true,
    },
    {
      platform: ECommercePlatforms.WIX,
      icon: "/assets/images/ecommerce/wix.svg",
      active: enabledPlatforms?.wix,
      visible: false,
    },
  ]

  useEffect(() => {
    fetchEcommerceIntegrations()
  }, [])

  useEffect(() => {
    fetchEcommerceIntegrations()
    setStoreFocus(null)
  }, [user.org.id])

  return (
    <SettingSection title={t("common.ecommerce.ecommerceIntegrations")} isBeta={false}>
      {isLoading ? (
        <SettingContentWrapper>
          <Loader fontSize={48} />
        </SettingContentWrapper>
      ) : (
        <>
          {isEmpty(storeFocus) || !storeFocus ? (
            platformsConfig
              ?.filter((platform) => platform.visible)
              .map((config) => {
                return (
                  <SettingContentWrapper
                    className={`${!config.active && "disabled-card cursor-not-allowed"}`}
                  >
                    <EcommercePlatformsList
                      config={config}
                      ecommerceIntegrations={ecommerceIntegrations?.filter(
                        (integration) => integration.platform === config.platform
                      )}
                      setStoreFocus={setStoreFocus}
                      setEcommerceIntegrations={setEcommerceIntegrations}
                    />
                  </SettingContentWrapper>
                )
              })
          ) : (
            <EcommerceShopSettings
              ecommerceIntegration={storeFocus}
              resetStoreFocus={() => setStoreFocus(null)}
            />
          )}
        </>
      )}
    </SettingSection>
  )
}

export default EcommerceSettings
